import React, { useState } from 'react';
import { MapPin, CheckCircle, AlertTriangle, Loader, Navigation } from 'lucide-react';
import { getCurrentPosition } from '../utils/geolocation';

interface LocationCheckInProps {
  onCheckIn: (location: { latitude: number; longitude: number; accuracy: number }) => Promise<void>;
  appointmentAddress?: string;
  disabled?: boolean;
}

export const LocationCheckIn: React.FC<LocationCheckInProps> = ({
  onCheckIn,
  appointmentAddress,
  disabled = false,
}) => {
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'requesting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);

  const handleGetLocation = async () => {
    setGettingLocation(true);
    setLocationStatus('requesting');
    setError(null);
    setProgress(0);
    setAccuracy(null);

    // Simulate progress while waiting for GPS
    const progressInterval = setInterval(() => {
      setProgress((prev) => Math.min(prev + 10, 90));
    }, 500);

    try {
      // Request location (native: Capacitor Geolocation; web: navigator.geolocation)
      const position = await getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });

      clearInterval(progressInterval);
      setProgress(100);

      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      };

      setAccuracy(position.coords.accuracy);
      setLocationStatus('success');

      // Brief pause to show success animation
      await new Promise(resolve => setTimeout(resolve, 800));
      await onCheckIn(location);
    } catch (err: any) {
      clearInterval(progressInterval);
      console.error('Location error:', err);
      setLocationStatus('error');

      // Provide user-friendly error messages
      let errorMessage = 'Failed to get your location. ';

      if (err.code === 1) {
        errorMessage += 'Please enable location permissions in your browser settings.';
      } else if (err.code === 2) {
        errorMessage += 'Your device location is unavailable. Please check your GPS settings.';
      } else if (err.code === 3) {
        errorMessage += 'Location request timed out. Please try again.';
      } else {
        errorMessage += err.message || 'Please try again.';
      }

      setError(errorMessage);
    } finally {
      setGettingLocation(false);
    }
  };

  const getStatusIcon = () => {
    const iconStyle = { width: '20px', height: '20px' };
    switch (locationStatus) {
      case 'requesting':
        return <Loader style={{ ...iconStyle, color: '#4F6367', animation: 'spin 1s linear infinite' }} />;
      case 'success':
        return <CheckCircle style={{ ...iconStyle, color: '#16a34a' }} />;
      case 'error':
        return <AlertTriangle style={{ ...iconStyle, color: '#dc2626' }} />;
      default:
        return <Navigation style={{ ...iconStyle, color: '#6b6b6b' }} />;
    }
  };

  const getStatusText = () => {
    switch (locationStatus) {
      case 'requesting':
        return 'Getting your location...';
      case 'success':
        return 'Location obtained successfully!';
      case 'error':
        return 'Failed to get location';
      default:
        return 'Ready to check in';
    }
  };

  const getStatusContainerStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      border: '2px solid',
      borderRadius: '8px',
      padding: '16px',
      transition: 'all 0.3s',
    };
    switch (locationStatus) {
      case 'success':
        return { ...base, borderColor: '#86efac', backgroundColor: '#f0fdf4', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' };
      case 'error':
        return { ...base, borderColor: '#fca5a5', backgroundColor: '#fef2f2' };
      case 'requesting':
        return { ...base, borderColor: '#93c5fd', backgroundColor: '#eff6ff' };
      default:
        return { ...base, borderColor: '#e5e0db', backgroundColor: '#f9fafb' };
    }
  };

  const getStatusTextStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = { fontSize: '14px', fontWeight: 'bold' };
    switch (locationStatus) {
      case 'success':
        return { ...base, color: '#14532d' };
      case 'error':
        return { ...base, color: '#7f1d1d' };
      case 'requesting':
        return { ...base, color: '#1e3a8a' };
      default:
        return { ...base, color: '#374151' };
    }
  };

  const getButtonStyle = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      width: '100%',
      padding: '16px 24px',
      borderRadius: '8px',
      fontWeight: 'bold',
      fontSize: '18px',
      transition: 'all 0.3s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      border: 'none',
      cursor: 'pointer',
    };

    if (locationStatus === 'success') {
      return {
        ...base,
        backgroundColor: '#16a34a',
        color: 'white',
        cursor: 'default',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        transform: 'scale(1.05)',
      };
    }

    if (gettingLocation) {
      return {
        ...base,
        backgroundColor: '#3b82f6',
        color: 'white',
        cursor: 'wait',
      };
    }

    if (disabled) {
      return {
        ...base,
        backgroundColor: '#4F6367',
        color: 'white',
        opacity: 0.5,
        cursor: 'not-allowed',
      };
    }

    return {
      ...base,
      backgroundColor: '#4F6367',
      color: 'white',
    };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Appointment Address Info */}
      {appointmentAddress && (
        <div style={{
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <MapPin style={{ width: '20px', height: '20px', color: '#4F6367', marginTop: '2px', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '14px', fontWeight: '500', color: '#1e3a8a', margin: 0 }}>Appointment Location</p>
              <p style={{ fontSize: '14px', color: '#3d4d50', marginTop: '4px' }}>{appointmentAddress}</p>
            </div>
          </div>
        </div>
      )}

      {/* Status Display */}
      <div style={getStatusContainerStyle()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {getStatusIcon()}
          <div style={{ flex: 1 }}>
            <span style={getStatusTextStyle()}>
              {getStatusText()}
            </span>

            {/* GPS Accuracy Display */}
            {accuracy !== null && locationStatus === 'success' && (
              <div style={{ marginTop: '4px', fontSize: '12px', color: '#15803d' }}>
                GPS Accuracy: ±{accuracy.toFixed(1)} meters
                {accuracy <= 10 && <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>Excellent</span>}
                {accuracy > 10 && accuracy <= 50 && <span style={{ marginLeft: '8px' }}>Good</span>}
                {accuracy > 50 && <span style={{ marginLeft: '8px' }}>Fair</span>}
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        {locationStatus === 'requesting' && (
          <div style={{ marginTop: '12px' }}>
            <div style={{
              width: '100%',
              backgroundColor: '#bfdbfe',
              borderRadius: '9999px',
              height: '8px',
              overflow: 'hidden',
            }}>
              <div
                style={{
                  backgroundColor: '#4F6367',
                  height: '8px',
                  transition: 'width 0.5s ease-out',
                  width: `${progress}%`,
                }}
              />
            </div>
            <div style={{ fontSize: '12px', color: '#3d4d50', marginTop: '4px', textAlign: 'center' }}>
              {progress < 30 && 'Requesting GPS permission...'}
              {progress >= 30 && progress < 60 && 'Acquiring satellite signal...'}
              {progress >= 60 && progress < 90 && 'Calculating position...'}
              {progress >= 90 && 'Finalizing...'}
            </div>
          </div>
        )}

        {error && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#991b1b',
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Unable to check in</div>
            {error}
          </div>
        )}
      </div>

      {/* Check In Button */}
      <button
        onClick={handleGetLocation}
        disabled={disabled || gettingLocation || locationStatus === 'success'}
        style={getButtonStyle()}
      >
        {locationStatus === 'success' ? (
          <>
            <CheckCircle style={{ width: '24px', height: '24px' }} />
            Check-In Successful!
          </>
        ) : gettingLocation ? (
          <>
            <Loader style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
            Getting Location...
          </>
        ) : (
          <>
            <MapPin style={{ width: '24px', height: '24px' }} />
            I'm Here - Check In
          </>
        )}
      </button>

      {/* Info Text */}
      <div style={{ fontSize: '12px', color: '#6b6b6b' }}>
        <p style={{ marginBottom: '4px' }}>• Your location will be verified against the appointment address</p>
        <p style={{ marginBottom: '4px' }}>• High accuracy GPS is required for check-in</p>
        <p>• Location data is stored for compliance purposes</p>
      </div>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
